import { Link, useRouterState } from '@tanstack/react-router'
import {
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  type LucideIcon,
  Truck,
  Users,
} from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * Vertical sidebar — desktop chrome.
 *
 * - Two widths: 240 px expanded, 64 px collapsed (icons only).
 *   The collapse state is local component state for now; once we
 *   ship a "remember last layout" preference per admin, lift it
 *   to the auth store or `localStorage`.
 * - 5 nav items mapped to the planned routes. The "active" item
 *   is highlighted with the brand colour; the rest stay muted to
 *   keep the eye drawn to the active page.
 * - Avity logo + "v2" stamp anchor the chrome top/bottom.
 *
 * Links use TanStack Router's `<Link>` so prefetch / typing apply.
 */

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/planning', label: 'Planning', icon: CalendarDays },
  { to: '/missions', label: 'Missions', icon: ClipboardList },
  { to: '/chauffeurs', label: 'Chauffeurs', icon: Users },
  { to: '/vehicules', label: 'Véhicules', icon: Truck },
  { to: '/clients', label: 'Clients', icon: Building2 },
]

export function Sidebar() {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const [collapsed, setCollapsed] = useState(false)

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30',
        'flex flex-col',
        'bg-sidebar text-sidebar-fg border-r border-sidebar-hover',
        'transition-[width] duration-150',
        collapsed ? 'w-16' : 'w-60',
      )}
      aria-label="Navigation principale"
    >
      {/* Brand block */}
      <div
        className={cn(
          'h-14 flex items-center border-b border-sidebar-hover',
          collapsed ? 'justify-center px-2' : 'px-4 gap-3',
        )}
      >
        <div
          aria-hidden="true"
          className="w-9 h-9 rounded-md bg-brand text-brand-fg inline-flex items-center justify-center font-bold shrink-0"
        >
          A
        </div>
        {!collapsed ? (
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-semibold text-sidebar-fg-active">Avity</span>
            <span className="text-xs text-sidebar-fg-muted">Administration</span>
          </div>
        ) : null}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        <ul className="flex flex-col gap-0.5 px-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon
            const active =
              currentPath === item.to || currentPath.startsWith(`${item.to}/`)
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={cn(
                    'flex items-center gap-3 rounded-md',
                    'h-10 px-3 text-sm font-medium',
                    'transition-colors duration-100',
                    active
                      ? 'bg-sidebar-active text-sidebar-fg-active'
                      : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-fg-active',
                    collapsed && 'justify-center px-0',
                  )}
                  // Hide the label visually when collapsed but
                  // keep it in DOM for screen readers — also
                  // doubles as a native tooltip on hover.
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={18} aria-hidden="true" />
                  {!collapsed ? <span>{item.label}</span> : null}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Footer: collapse toggle + version stamp */}
      <div
        className={cn(
          'border-t border-sidebar-hover',
          collapsed ? 'flex flex-col items-center py-2 gap-2' : 'flex items-center justify-between px-3 py-2',
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Développer la barre' : 'Réduire la barre'}
          className={cn(
            'inline-flex items-center justify-center',
            'h-8 w-8 rounded-md',
            'text-sidebar-fg-muted hover:bg-sidebar-hover hover:text-sidebar-fg-active',
            'transition-colors duration-100',
          )}
          title={collapsed ? 'Développer' : 'Réduire'}
        >
          {collapsed ? (
            <ChevronRight size={16} aria-hidden="true" />
          ) : (
            <ChevronLeft size={16} aria-hidden="true" />
          )}
        </button>
        {!collapsed ? (
          <span className="text-xs text-sidebar-fg-muted">v2</span>
        ) : null}
      </div>
    </aside>
  )
}

/**
 * Width helper used by the sibling content area to compute its
 * left offset. Kept here so the sidebar is the single source of
 * truth for its own dimensions.
 *
 * Returns the *expanded* width — the collapsed state happens
 * within the sidebar's own layer (`fixed inset-y-0 left-0`), so
 * the content area should always reserve room for the expanded
 * width to avoid layout jumps when the user toggles. If we ever
 * want the content to reflow on collapse, lift `collapsed` to
 * a context or store.
 */
export const SIDEBAR_WIDTH_PX = 240
